import { useEffect } from "react";
import { numeroALetras, redondearDecimales } from "../../../../utils/utils_number";

import { resumenCCFSchema, facturaCCFSchema, itemCCFSchema } from "./schemas/zod/ccf.schema";

const useCCF_JSON = (factura, setFactura) => {
  useEffect(() => {
    setFactura((prev) => ({
      ...prev,
      identificacion: {
        ...prev.identificacion,
        version: 3,
      },
      otrosDocumentos: null,
      resumen: {
        totalNoSuj: 0,
        totalExenta: 0,
        totalGravada: 0,
        subTotalVentas: 0, // Sumatoria de totalNoSuj + totalExenta + totalGravada
        descuNoSuj: 0,
        descuExenta: 0,
        descuGravada: 0,
        porcentajeDescuento: 0,
        totalDescu: 0,
        tributos: [], // ejemplo aplicando IVA: [{ codigo: "20", descripcion: "Impuesto al Valor Agregado 13%", valor: 2.71 }]
        subTotal: 0,
        ivaPerci1: 0,
        ivaRete1: 0,
        reteRenta: 0,
        montoTotalOperacion: 0,
        totalNoGravado: 0,
        totalPagar: 0,
        totalLetras: "",
        saldoFavor: 0,
        condicionOperacion: 2, // 1 contado, 2 credito, 3 otros
        pagos: null, // { codigo: "01", montoPago: 23.56, referencia: null, plazo: null, periodo: null }
        numPagoElectronico: "",
      },
    }));
  }, []);

  const itemBase = {
    numItem: 1,
    tipoItem: 1,
    descripcion: "",
    cantidad: "",
    uniMedida: 59, // Código de unidad de medida (Ej: Unidad)
    precioUni: "",
    ventaNoSuj: 0.0,
    ventaExenta: 0.0,
    ventaGravada: 0.0,
    tributos: null, // "20" Se aplica IVA

    // No ocupados de momento:
    codTributo: null,
    numeroDocumento: null,
    montoDescu: 0.0,
    codigo: null,
    psv: 0.0,
    noGravado: 0.0,
  };

  const formatearItem = (formData, tipoVenta, comprobanteRelacionado, index) => {
    const cantidad = redondearDecimales(parseFloat(formData.cantidad)) || 0;
    const precioUnitario = redondearDecimales(parseFloat(formData.precioUni)) || 0;
    const montoTotal = redondearDecimales(cantidad * precioUnitario);

    let item = {
      ...formData,
      numItem: index,
      cantidad,
      precioUni: precioUnitario,
      ventaGravada: 0,
      ventaNoSuj: 0,
      ventaExenta: 0,
      tributos: null,
    };

    if (tipoVenta === "1") {
      item.ventaGravada = montoTotal;
      item.tributos = ["20"]; // IVA 13%
    } else if (tipoVenta === "2") {
      item.ventaNoSuj = montoTotal;
    } else if (tipoVenta === "3") {
      item.ventaExenta = montoTotal;
    }

    if (comprobanteRelacionado) {
      item.numeroDocumento = comprobanteRelacionado.codigoGeneracion || null;
    }

    return itemCCFSchema.parse(item);
  };

  // Función para asignar resumen de totales
  const setResumen = (resumen) => {
    const subTotalVentas = redondearDecimales(resumen.totalNoSuj + resumen.totalExenta + resumen.totalGravada, 2);
    const totalValorTributo = resumen?.tributos?.reduce((acc, tributo) => acc + tributo.valor, 0);
    const montoTotalOperacion = redondearDecimales(subTotalVentas + totalValorTributo, 2);
    const totalPagar = redondearDecimales(montoTotalOperacion - resumen.ivaRete1, 2);

    const resumenValidado = resumenCCFSchema.parse({
      ...resumen,
      subTotalVentas: subTotalVentas,
      subTotal: subTotalVentas, // Hay que restar los descuentos de descuNoSuj + descuExenta + descuGravada
      montoTotalOperacion: montoTotalOperacion,
      totalPagar: totalPagar, // Valor real debe contemplar IVA percibido, retencion de renta y noGravado
      totalLetras: numeroALetras(totalPagar),
      condicionOperacion: resumen.formaPago?.value || 2,
      pagos:
        resumen.formaPago?.value === 1
          ? null
          : [
              {
                codigo: "01",
                montoPago: totalPagar,
                referencia: null,
                plazo: "01",
                periodo: parseInt(resumen.formaPago.diascredito),
              },
            ],
    });

    setFactura((prev) => ({
      ...prev,
      resumen: resumenValidado,
    }));
  };

  const buildJson = () => {
    return facturaCCFSchema.parse(factura);
  };

  return {
    itemBase,
    formatearItem,
    setResumen,
    buildJson,
  };
};

export default useCCF_JSON;
